import React from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { numberWithComma, formatDateWithoutTime, currentDateTimeThaiformat } from 'helpers/format'
import { Col, Image, Row, Space } from 'antd'
import { PurchaseData, PurchaseProduct } from 'Store/purchase/types'
import { appConfig } from 'config'
import { ImageFallback } from 'helpers/constanst'
import PdfCompanyInfo from './PdfCompanyInfo'
import { PdfPrintFormat, PdfPrintLiteFormat } from 'Components/form'

type Props = {
  detail?: PurchaseData
  isLite?: boolean
}

const PurchasePDF: React.FunctionComponent<Props> = props => {
  const { detail, isLite = false } = props

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const renderImage = (item: PurchaseProduct) => (
    <ThumbImage src={appConfig.assetCdnEndpoint + item.product_photo} fallback={ImageFallback} />
  )

  const renderInfoItem = (title: any, value: any, justify: 'start' | 'end' = 'start') => {
    return (
      <Row gutter={[8, 0]} justify={justify}>
        <Col>
          <Space wrap={true}>
            <InfoTitle>
              {title} : {value || '-'}
            </InfoTitle>
          </Space>
        </Col>
      </Row>
    )
  }

  const renderSupplierInfo = () => {
    const supplier = detail?.supplier
    return (
      <div>
        {renderInfoItem('ผู้จำหน่าย', supplier?.supplier_code)}
        {renderInfoItem('บริษัท', supplier?.name)}
        {renderInfoItem('ที่อยู่', supplier?.address)}
        {renderInfoItem('เบอร์โทรศัพท์', supplier?.mobile_no)}
      </div>
    )
  }

  const renderPurchaseInfo = () => {
    return (
      <div>
        {renderInfoItem('เลขที่', detail?.purchasing_no, 'end')}
        {renderInfoItem('วันที่', formatDateWithoutTime(detail?.purchasing_date || ''), 'end')}
        {renderInfoItem('ผู้สร้างรายการ', detail?.purchasing_by, 'end')}
        {renderInfoItem('วันที่พิมพ์', currentDateTimeThaiformat(), 'end')}
      </div>
    )
  }

  const renderProductName = (item: any, record: PurchaseProduct) => {
    const isSameTitle = record.product_name == record.product_option_name
    return (
      <>
        <RowTile>{record.product_name}</RowTile>
        {!isSameTitle && <ProductDesc>{record.product_option_name}</ProductDesc>}
      </>
    )
  }

  const renderTotalPrice = (item: PurchaseProduct) => {
    return (item.amount || 0) * (item.price_per_unit || 0)
  }

  const countTotal = () => {
    let amount = 0
    let price = 0
    detail?.purchasing_item &&
      detail.purchasing_item.forEach(item => {
        const totalPrice = (item.amount || 0) * (item.price_per_unit || 0)
        amount += item.amount
        price += totalPrice
      })
    return { amount, price }
  }

  let title = 'ใบสั่งซื้อสินค้า'
  if (detail && detail?.status === 'draft') {
    title = 'ใบสั่งซื้อสินค้า (ชั่วคราว)'
  }

  const summaryFooter = (
    <Row justify="space-between" gutter={[24, 8]}>
      <Col span={12}>{renderInfoItem('หมายเหตุ', detail?.remark)}</Col>
      <Col span={10}>
        <Row justify="space-between">
          <Col>
            <InfoTitle>จำนวนเงินรวมทั้งสิ้น</InfoTitle>
          </Col>
          <Col>
            <InfoTitle>{numberWithComma(countTotal().price, true)} บาท</InfoTitle>
          </Col>
        </Row>
      </Col>
    </Row>
  )

  const tableHeader = (
    <thead>
      <th>ลำดับ</th>
      {!isLite && <th style={{ width: '50px' }}>รูปภาพ</th>}
      {/* <th className="left">รหัสสินค้า</th> */}
      <th style={{ width: '20%' }} className="left">
        รายการ
      </th>
      <th className="left">ยี่ห้อ</th>
      <th>จำนวน</th>
      <th>หน่วย</th>
      <th className="right">ต้นทุนต่อหน่วย</th>
      <th className="right">ราคารวม</th>
    </thead>
  )

  const tableBody = (
    <tbody>
      {detail?.purchasing_item &&
        detail.purchasing_item.map((item: PurchaseProduct, index: number) => {
          return (
            <tr key={index}>
              <td>{renderInfo(index + 1)}</td>
              {!isLite && <td>{renderImage(item)}</td>}
              {/* <td className="left">{renderInfo(item?.product_sku || '-')}</td> */}
              <td className="left">{renderProductName(item?.product_name, item)}</td>
              <td className="left">{renderInfo(item?.product_manufacture || '-')}</td>
              <td>{renderInfo(numberWithComma(item?.amount))}</td>
              <td>{renderInfo(item?.product_uom || '-')}</td>
              <td className="right">{renderInfo(numberWithComma(item?.price_per_unit || 0, true))}</td>
              <td className="right">{renderInfo(numberWithComma(renderTotalPrice(item), true))}</td>
            </tr>
          )
        })}
    </tbody>
  )

  return isLite ? (
    <PdfPrintLiteFormat
      companyInfo={<PdfCompanyInfo merchant={detail?.merchant} />}
      title={title}
      leftInfo={renderSupplierInfo()}
      rightInfo={renderPurchaseInfo()}
      footerSpan={9}
      summaryFooter={summaryFooter}
      signPosition={['ผู้ออกใบสั่งซื้อ', 'ผู้อนุมัติใบสั่งซื้อ']}
      thead={tableHeader}
      tbody={tableBody}
    />
  ) : (
    <PdfPrintFormat
      companyInfo={<PdfCompanyInfo merchant={detail?.merchant} />}
      title={title}
      leftInfo={renderSupplierInfo()}
      rightInfo={renderPurchaseInfo()}
      footerSpan={9}
      summaryFooter={summaryFooter}
      signPosition={['ผู้ออกใบสั่งซื้อ', 'ผู้อนุมัติใบสั่งซื้อ']}
      thead={tableHeader}
      tbody={tableBody}
    />
  )
}

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 40px;
  height: 40px;
  object-fit: contain;
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const InfoTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  white-space: pre-line;
  text-align: left;
`

export default PurchasePDF
